import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  exportAvomanCalibrationBatch,
  listAvomanCalibrationTurns,
  reviewAvomanCalibrationTurn,
  type AvomanCalibrationBatch,
  type AvomanCalibrationTurn,
  type AvomanClassification,
  type AvomanReviewStatus,
} from '../lib/admin-avoman-calibration-api';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';

const REVIEW_STATUSES: Array<{ value: AvomanReviewStatus; label: string }> = [
  { value: 'pending', label: '待审核' },
  { value: 'accepted', label: '已接受' },
  { value: 'rejected', label: '已拒绝' },
];

const CLASSIFICATIONS: Array<{ value: AvomanClassification; label: string }> = [
  { value: 'boundary', label: '边界' },
  { value: 'knowledge', label: '知识' },
  { value: 'style', label: '风格' },
  { value: 'eval_candidate', label: '好例子' },
  { value: 'mixed', label: '混合' },
  { value: 'general', label: '普通' },
];

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('zh-CN', {
    hour12: false,
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusTone(status: AvomanReviewStatus): string {
  if (status === 'accepted') {
    return 'border-emerald-300 bg-emerald-50 text-emerald-800';
  }
  if (status === 'rejected') {
    return 'border-rose-300 bg-rose-50 text-rose-800';
  }
  return 'border-amber-300 bg-amber-50 text-amber-800';
}

export default function AvomanCalibrationReviewPage() {
  const accessToken = useAdminAuthStore((state) => state.accessToken);
  const [reviewStatus, setReviewStatus] = useState<AvomanReviewStatus>('pending');
  const [turns, setTurns] = useState<AvomanCalibrationTurn[]>([]);
  const [classificationEdits, setClassificationEdits] = useState<Record<string, AvomanClassification>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [busyTurnId, setBusyTurnId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [batch, setBatch] = useState<AvomanCalibrationBatch | null>(null);

  const selectedStatusLabel = useMemo(
    () => REVIEW_STATUSES.find((item) => item.value === reviewStatus)?.label ?? reviewStatus,
    [reviewStatus],
  );

  const loadTurns = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const nextTurns = await listAvomanCalibrationTurns(accessToken, {
        reviewStatus,
        limit: 100,
      });
      setTurns(nextTurns);
      setClassificationEdits(
        Object.fromEntries(nextTurns.map((turn) => [turn.id, turn.classification])),
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '加载校准 turns 失败');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, reviewStatus]);

  useEffect(() => {
    void loadTurns();
  }, [loadTurns]);

  async function handleReview(turn: AvomanCalibrationTurn, nextStatus: AvomanReviewStatus) {
    if (!accessToken) {
      return;
    }
    setBusyTurnId(turn.id);
    setErrorMessage(null);
    try {
      await reviewAvomanCalibrationTurn(accessToken, turn.id, {
        reviewStatus: nextStatus,
        classification: classificationEdits[turn.id] ?? turn.classification,
      });
      await loadTurns();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '审核失败');
    } finally {
      setBusyTurnId(null);
    }
  }

  async function handleExport() {
    if (!accessToken) {
      return;
    }
    setIsExporting(true);
    setErrorMessage(null);
    try {
      const nextBatch = await exportAvomanCalibrationBatch(accessToken, {
        reviewStatus,
        limit: 500,
        includeRejected: false,
      });
      setBatch(nextBatch);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '导出失败');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <main className="min-h-screen bg-stone-100 text-stone-950">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <header className="flex flex-col gap-4 border-b border-stone-300 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-medium text-emerald-700">牛博士校准后台</p>
            <h1 className="mt-2 text-3xl font-semibold">Avoman 校准审核</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-stone-600">
              人工检查待审核 turns，确认分类后接受或拒绝；导出批次仍只读取已选择的审核状态。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-medium text-stone-700 shadow-sm transition hover:border-emerald-400 hover:text-emerald-800"
              to="/admin"
            >
              返回后台
            </Link>
            <button
              className="rounded-lg border border-emerald-700 bg-emerald-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isExporting || !accessToken}
              onClick={handleExport}
              type="button"
            >
              {isExporting ? '导出中...' : '导出当前批次'}
            </button>
          </div>
        </header>

        <section className="flex flex-col gap-3 border-b border-stone-300 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="审核状态">
            {REVIEW_STATUSES.map((item) => (
              <button
                aria-selected={reviewStatus === item.value}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  reviewStatus === item.value
                    ? 'border-stone-950 bg-stone-950 text-white'
                    : 'border-stone-300 bg-white text-stone-700 hover:border-stone-500'
                }`}
                key={item.value}
                onClick={() => setReviewStatus(item.value)}
                role="tab"
                type="button"
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="text-sm text-stone-600">
            {isLoading ? '加载中' : `${selectedStatusLabel} ${turns.length} 条`}
          </div>
        </section>

        {errorMessage ? (
          <div className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {errorMessage}
          </div>
        ) : null}

        {batch ? (
          <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-base font-semibold text-emerald-950">导出结果</h2>
              <p className="text-sm text-emerald-800">
                {String(batch.batch_id ?? 'batch')} · {String(batch.count ?? 0)} 条
              </p>
            </div>
            <pre className="mt-3 max-h-72 overflow-auto rounded-lg bg-white p-3 text-xs leading-5 text-stone-800">
              {JSON.stringify(batch, null, 2)}
            </pre>
          </section>
        ) : null}

        <section className="grid gap-3">
          {!isLoading && turns.length === 0 ? (
            <div className="rounded-lg border border-stone-300 bg-white px-4 py-10 text-center text-sm text-stone-600">
              当前状态没有待处理 turns。
            </div>
          ) : null}

          {turns.map((turn) => (
            <article
              aria-label={turn.id}
              className="rounded-lg border border-stone-300 bg-white p-4 shadow-sm"
              key={turn.id}
            >
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-lg border px-2 py-1 text-xs font-semibold ${statusTone(turn.reviewStatus)}`}>
                      {turn.reviewStatus}
                    </span>
                    <span className="text-sm text-stone-500">{formatDateTime(turn.createdAt)}</span>
                    <span className="text-sm text-stone-500">session {turn.calibrationSessionId}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {turn.tags.length > 0 ? (
                      turn.tags.map((tag) => (
                        <span className="rounded-lg bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-900" key={tag}>
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="rounded-lg bg-stone-100 px-2 py-1 text-xs font-semibold text-stone-600">
                        无标签
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="text-sm font-medium text-stone-600" htmlFor={`classification-${turn.id}`}>
                    分类
                  </label>
                  <select
                    className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-800"
                    id={`classification-${turn.id}`}
                    onChange={(event) =>
                      setClassificationEdits((current) => ({
                        ...current,
                        [turn.id]: event.target.value as AvomanClassification,
                      }))
                    }
                    value={classificationEdits[turn.id] ?? turn.classification}
                  >
                    {CLASSIFICATIONS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <button
                    className="rounded-lg border border-emerald-700 bg-emerald-700 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={busyTurnId === turn.id}
                    onClick={() => void handleReview(turn, 'accepted')}
                    type="button"
                  >
                    接受
                  </button>
                  <button
                    className="rounded-lg border border-rose-700 bg-white px-3 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={busyTurnId === turn.id}
                    onClick={() => void handleReview(turn, 'rejected')}
                    type="button"
                  >
                    拒绝
                  </button>
                </div>
              </div>

              <div className="mt-4 grid gap-3 lg:grid-cols-2">
                <div>
                  <h2 className="text-sm font-semibold text-stone-700">校准者输入</h2>
                  <p className="mt-2 whitespace-pre-wrap rounded-lg bg-stone-100 p-3 text-sm leading-6 text-stone-900">
                    {turn.userMessage}
                  </p>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-stone-700">牛博士回复</h2>
                  <p className="mt-2 whitespace-pre-wrap rounded-lg bg-stone-950 p-3 text-sm leading-6 text-stone-50">
                    {turn.assistantAnswer}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </section>
      </div>
    </main>
  );
}
